import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Alert } from 'antd';
import lowerCase from 'lodash/lowerCase';

import { requestOtp, confirmOtp } from './actions';
import {
    selectIsOtpLoading,
    selectIsLoginLoading,
    selectOtpError,
    selectLoginError,
    selectOtpSession,
    selectOtpPhone,
} from './selectors';
import PhoneCountryMask from '../address/PhoneCountryMask';
import { fetchCountries, addressSelectors } from '../address';

const STEPS = {
    PHONE: 'phone',
    OTP: 'otp',
};

const LoginForm = ({ onSwitchToRegister, onSuccess }) => {
    const dispatch = useDispatch();
    const isOtpLoading = useSelector(selectIsOtpLoading);
    const isLoginLoading = useSelector(selectIsLoginLoading);
    const otpError = useSelector(selectOtpError);
    const loginError = useSelector(selectLoginError);
    const otpSession = useSelector(selectOtpSession);
    const otpPhone = useSelector(selectOtpPhone);

    const countries = useSelector((state) => addressSelectors.selectCountries(state));
    const isFetching = useSelector((state) => addressSelectors.selectIsCountriesFetching(state));

    const [step, setStep] = useState(STEPS.PHONE);
    const [phone, setPhone] = useState('');
    const [phoneCountry, setPhoneCountry] = useState('RU');
    const [otpInputs, setOtpInputs] = useState(['', '', '', '']);
    const [errors, setErrors] = useState({});

    const [repeatTimeout, setRepeatTimeout] = useState(0);
    const [waitTimeout, setWaitTimeout] = useState(0);

    useEffect(() => {
        if (!countries || !countries.countries) {
            dispatch(fetchCountries());
        }
    }, [dispatch]);

    useEffect(() => {
        if (step !== STEPS.OTP || !otpSession) return;

        let repeatTime = otpSession.otp_get_timeout || 60;
        setRepeatTimeout(repeatTime);
        const repeatInterval = setInterval(() => {
            repeatTime--;
            setRepeatTimeout(repeatTime);
            if (repeatTime <= 0) clearInterval(repeatInterval);
        }, 1000);

        let waitTime = otpSession.otp_wait_timeout || 300;
        setWaitTimeout(waitTime);
        const waitInterval = setInterval(() => {
            waitTime--;
            setWaitTimeout(waitTime);
            if (waitTime <= 0) {
                clearInterval(waitInterval);
                setStep(STEPS.PHONE);
                setOtpInputs(['', '', '', '']);
            }
        }, 1000);

        setTimeout(() => {
            const el = document.getElementById('otp-input-0');
            if (el) el.focus();
        }, 100);

        return () => {
            clearInterval(repeatInterval);
            clearInterval(waitInterval);
        };
    }, [step, otpSession]);

    let listPhoneCountries = [];
    if (!isFetching && countries && countries.countries) {
        listPhoneCountries = Object.values(countries.countries)
            .filter((el) => el.iso === 'RU')
            .map((element, index) => ({
                key: index,
                text: element.country_ru,
                flag: lowerCase(element.iso),
                value: element.iso,
                className: 'dropdown-item',
                phone_mask: element.phone_mask,
            }));
    }

    const handleChangeField = (fieldName, value) => {
        if (fieldName === 'phone_country') {
            setPhoneCountry(value);
        } else {
            setPhone(value);
            setErrors({});
        }
    };

    const validatePhone = () => {
        const newErrors = {};
        if (!phone.trim() || phone.includes('_')) {
            newErrors.phone = 'Введите телефон полностью';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRequestOtp = async () => {
        if (!validatePhone()) return;

        const result = await dispatch(requestOtp({ phone }));

        if (result?.success) {
            setStep(STEPS.OTP);
            setOtpInputs(['', '', '', '']);
        }
    };

    const handleResendOtp = async () => {
        if (repeatTimeout > 0) return;
        await dispatch(requestOtp({ phone: otpPhone || phone }));
        setOtpInputs(['', '', '', '']);
    };

    const handleOtpInput = useCallback((index, value) => {
        if (value && !/^\d$/.test(value)) return;

        const newInputs = [...otpInputs];
        newInputs[index] = value;
        setOtpInputs(newInputs);

        if (value && index < 3) {
            const next = document.getElementById(`otp-input-${index + 1}`);
            if (next) next.focus();
        }

        const code = newInputs.join('');
        if (code.length === 4) {
            dispatch(confirmOtp({
                phone: otpPhone || phone,
                idSession: otpSession?.id_session,
                otpCode: code,
            })).then((result) => {
                if (result?.success && onSuccess) {
                    onSuccess();
                }
            });
        }
    }, [otpInputs, otpPhone, phone, otpSession, dispatch, onSuccess]);

    const handleOtpKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otpInputs[index] && index > 0) {
            const prev = document.getElementById(`otp-input-${index - 1}`);
            if (prev) prev.focus();
        }
    };

    const handleBackToPhone = () => {
        setStep(STEPS.PHONE);
        setOtpInputs(['', '', '', '']);
    };

    const serverError = step === STEPS.PHONE ? otpError : loginError;

    return (
        <div className="auth-form">
            {serverError && (
                <Alert
                    message={serverError}
                    type="error"
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                />
            )}

            {step === STEPS.PHONE ? (
                <>
                    <div className="auth-fields">
                        <div className="auth-field">
                            <label className="auth-label">Телефон</label>
                            <PhoneCountryMask
                                extraClass="auth-phone-mask"
                                countries={listPhoneCountries}
                                country={phoneCountry}
                                loading={isFetching}
                                phone={phone}
                                handleChangeField={handleChangeField}
                                nameCountry="phone_country"
                                namePhone="phone"
                                fluid={true}
                            />
                            {errors.phone && <span className="auth-field-error">{errors.phone}</span>}
                        </div>
                    </div>

                    <Button
                        className="auth-submit-btn"
                        size="large"
                        block
                        onClick={handleRequestOtp}
                        loading={isOtpLoading}
                    >
                        Получить код
                    </Button>
                </>
            ) : (
                <>
                    <div className="auth-otp-info">
                        <span className="auth-otp-text">Введите код, отправленный на</span>
                        <span className="auth-otp-phone">{otpPhone || phone}</span>
                    </div>

                    <div className="auth-otp-inputs">
                        {otpInputs.map((value, index) => (
                            <input
                                key={index}
                                id={`otp-input-${index}`}
                                className="auth-otp-input"
                                type="tel"
                                maxLength={1}
                                autoComplete="off"
                                value={value}
                                onChange={(e) => handleOtpInput(index, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                            />
                        ))}
                    </div>

                    <div className="auth-otp-actions">
                        <button
                            className="auth-otp-resend"
                            onClick={handleResendOtp}
                            disabled={repeatTimeout > 0}
                        >
                            {repeatTimeout > 0
                                ? `Отправить повторно (${repeatTimeout} сек)`
                                : 'Отправить повторно'
                            }
                        </button>
                    </div>

                    {isLoginLoading && (
                        <div className="auth-otp-loading">Проверяем код...</div>
                    )}

                    <Button
                        className="auth-submit-btn auth-back-btn"
                        size="large"
                        block
                        onClick={handleBackToPhone}
                        style={{ marginTop: 16 }}
                    >
                        Изменить номер
                    </Button>
                </>
            )}

            <div className="auth-switch-text">
                Нет аккаунта?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); onSwitchToRegister(); }}>
                    Зарегистрироваться
                </a>
            </div>
        </div>
    );
};

export default LoginForm;
